// Image field of the aldoo.com admin. Every media picker goes through the
// framework AddMediaDialog (drop / paste ⌘V / image url → crop → upload)
// instead of a bare file input: a preview box that opens the dialog, an
// Upload button, an optional "Library" pick and Clear.
//
// The call site keeps its own endpoint: `upload(file, mimeType)` gets a File
// (a Blob with a name) built from the cropped image, so the multipart helpers
// in AdminShared/api.js stay unchanged.
import React, { useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import Button from "aldoo-ra/Button"
import AddMediaDialog from "aldoo-ra/AddMediaDialog"
import { PhotoIcon, ArrowUpTrayIcon, FolderOpenIcon, TrashIcon } from "@heroicons/react/24/outline"
import { errorMessage } from "./ui"

/**
 * Cropped blob → exactly `size` ("1024x1024"). The dialog crops on that ratio
 * already (AddMediaDialog `aspect`), so this only scales to the exact pixels
 * the API demands (ProductCreator.thumbnail 300x300, Product.square 1024x1024…,
 * tolerance 0.01) and centre-fills anything a rounding left over.
 */
const fitToSize = (blob, size, mimeType = "image/webp") =>
  new Promise((resolve) => {
    const [w, h] = String(size || "").split(/[x×]/).map((n) => Number(String(n).trim()))
    if (!w || !h) return resolve(blob)
    const url = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = () => {
      const scale = Math.max(w / img.width, h / img.height)
      const dw = img.width * scale
      const dh = img.height * scale
      const canvas = document.createElement("canvas")
      canvas.width = w
      canvas.height = h
      canvas.getContext("2d").drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh)
      canvas.toBlob((out) => {
        URL.revokeObjectURL(url)
        resolve(out || blob)
      }, mimeType, 0.9)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(blob)
    }
    img.src = url
  })

/** Cropped blob → a named File the multipart upload helpers can send. */
const mediaFile = (blob, mimeType = "image/webp", name = "image") => {
  const ext = String(mimeType).split("/")[1]?.split("+")[0] || "webp"
  const safe = String(name).replace(/[^a-z0-9._-]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase() || "image"
  return new File([blob], `${safe}-${Date.now()}.${ext}`, { type: mimeType })
}

export default function MediaField({
  label,
  hint,
  src,
  name = "image",
  alt = "",
  size,
  upload,
  onUploaded,
  formatError,
  onClear,
  onPickLibrary,
  libraryLabel = "Library",
  uploadLabel = "Upload",
  currentImageUrl = null,
  disabled = false,
  busy = false,
  preview = true,
  prompt,
  boxClassName = "w-[160px] h-[120px] rounded-md",
  boxStyle,
  imgClassName = "object-cover",
  className = "",
  "data-testid": testId = "media-field",
}) {
  const hintText = hint ?? size
  const [open, setOpen] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [error, setError] = useState("")
  // a file dropped on the preview opens the dialog straight in the crop step
  const [dropped, setDropped] = useState(null)
  const droppedRef = useRef(null)

  const close = () => {
    setOpen(false)
    if (droppedRef.current) URL.revokeObjectURL(droppedRef.current)
    droppedRef.current = null
    setDropped(null)
  }

  const openDialog = () => {
    if (disabled || busy) return
    setError("")
    setOpen(true)
  }

  const onDrop = (e) => {
    e.preventDefault()
    setDragging(false)
    if (disabled || busy) return
    const file = e.dataTransfer?.files?.[0]
    if (!file || !file.type.startsWith("image/")) return
    const url = URL.createObjectURL(file)
    droppedRef.current = url
    setDropped(url)
    setError("")
    setOpen(true)
  }

  const uploadFn = async (blob, mimeType) => {
    const fitted = size ? await fitToSize(blob, size, mimeType) : blob
    const res = await upload(mediaFile(fitted, mimeType, name), mimeType)
    const err = res?.error && formatError ? formatError(res) : errorMessage(res, res ? "" : "Upload failed")
    if (err) {
      setError(err)
      return { error: err }
    }
    setError("")
    return res
  }

  const showClear = !!onClear && !!src

  return (
    <div className={twMerge("flex flex-col gap-2 items-start", className)} data-testid={testId} data-media-state={src ? "image" : "empty"}>
      {label && <span className="text-xs font-medium text-admin_text_secondary">{label}</span>}

      {preview && (
        <div
          role="button"
          aria-disabled={disabled}
          aria-label={label ? `${label} image` : "image"}
          onClick={openDialog}
          onDragOver={(e) => {
            e.preventDefault()
            if (!disabled && !busy) setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          data-testid={`${testId}-preview`}
          style={boxStyle}
          className={twMerge(
            "relative group overflow-hidden flex items-center justify-center bg-admin_neutral_1 border-2 border-dashed border-admin_bordered transition-colors",
            disabled ? "cursor-default opacity-60" : "cursor-pointer hover:border-admin_primary",
            dragging ? "border-admin_primary" : "",
            boxClassName,
          )}
        >
          {src ? <img src={src} alt={alt || label || ""} className={twMerge("w-full h-full", imgClassName)} /> : null}
          {!src && (
            <div className="flex flex-col items-center gap-1 text-admin_text_secondary px-2 text-center">
              <PhotoIcon className="w-7 h-7" />
              {prompt ? typeof prompt === "string" ? <span className="text-xs font-semibold text-admin_text">{prompt}</span> : prompt : null}
            </div>
          )}
          {src && !busy && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-white/75 opacity-0 group-hover:opacity-100 transition-opacity text-admin_text">
              <ArrowUpTrayIcon className="w-6 h-6" />
              <span className="text-xs font-semibold">Replace</span>
            </div>
          )}
          {busy && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/70">
              <div className="w-7 h-7 border-4 border-admin_primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
      )}

      {hintText && <span className="text-[11px] text-admin_text_secondary">{hintText}</span>}

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="admin_secondary" className="h-[28px] text-xs px-2" onClick={openDialog} disabled={disabled || busy} data-testid={`${testId}-upload`}>
          <ArrowUpTrayIcon className="w-3.5 h-3.5" /> {busy ? "Uploading…" : uploadLabel}
        </Button>
        {onPickLibrary && (
          <Button variant="admin_secondary" className="h-[28px] text-xs px-2" onClick={onPickLibrary} disabled={disabled || busy} data-testid={`${testId}-library`}>
            <FolderOpenIcon className="w-3.5 h-3.5" /> {libraryLabel}
          </Button>
        )}
        {showClear && (
          <Button variant="admin_text" className="h-[28px] text-xs px-2 text-admin_error" onClick={onClear} disabled={disabled || busy} data-testid={`${testId}-clear`}>
            <TrashIcon className="w-3.5 h-3.5" /> Clear
          </Button>
        )}
      </div>

      {error && <span className="text-[11px] text-admin_error" data-testid={`${testId}-error`}>{error}</span>}

      {open && (
        <AddMediaDialog
          imagesOnly
          // fields with a fixed target size crop on that ratio (the fit below
          // then only scales, it never crops away part of the chosen area)
          aspect={size || null}
          currentImageUrl={dropped || currentImageUrl}
          uploadFn={uploadFn}
          onUse={(result) => {
            close()
            onUploaded?.(result)
          }}
          onCancel={close}
        />
      )}
    </div>
  )
}
